import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { setToken } from '@/Config/auth/authStore';

const CONSUME_NONCE_ENDPOINT = '/v1/api/auth/consume_callback_nonce';

function parseHashParams(hash: string): Record<string, string> {
  const params: Record<string, string> = {};
  const stripped = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!stripped) return params;

  const searchParams = new URLSearchParams(stripped);
  searchParams.forEach((value, key) => {
    params[key] = value;
  });
  return params;
}

export function AuthCallbackPage(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = parseHashParams(window.location.hash);
    const token = params.token;
    const scenarioId = params.scenario_id;
    const nonce = params.nonce;

    // Clear the URL fragment BEFORE any async work — keeps the token out of
    // browser history, popstate listeners, and analytics URL captures even
    // if the consume call is slow.
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    const bounceToLogin = () => {
      toast.info(t('session_expired'));
      navigate('/', { replace: true });
    };

    if (!token || !nonce) {
      bounceToLogin();
      return;
    }

    // Verify the JWT was minted for the current Django session before
    // committing it to the in-memory auth store: blocks login-CSRF where
    // an attacker lures a victim to /auth/callback with the attacker's JWT.
    void (async () => {
      try {
        await axios.post(CONSUME_NONCE_ENDPOINT, { token, nonce }, { withCredentials: true });
      } catch {
        bounceToLogin();
        return;
      }

      const stored = setToken(token);
      if (!stored) {
        bounceToLogin();
        return;
      }

      if (scenarioId) {
        navigate(`/i/${scenarioId}/`, { replace: true });
      } else {
        // Brand-new social-auth signup with no ChatImplementation yet.
        // SPA-navigating to `/` would mount BootstrapProvider with no
        // scenario_id and (depending on guards) flash a blank screen.
        // Defer to Django's `index()` view: it knows to redirect a
        // logged-in user with no impl to `/add_new_ai/` and create one.
        window.location.assign('/');
      }
    })();
  }, [navigate, t]);

  return (
    <div
      data-testid="auth-callback-loading"
      className="flex items-center justify-center h-screen w-screen"
    >
      <Loader2 className="animate-spin h-8 w-8 text-muted-foreground" />
    </div>
  );
}
